import type { Step as RcStep } from '@sb/remote-control/types';

import { type ValidationRule } from '../../types';
import { ActionRequiredError } from '../../util/errors';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';

class CallSubroutineRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.CallSubroutine', 'CallSubroutine');
  }

  protected validate({ args, routine, ctx }: CanonicalValidateArgs): void {
    type CSArgs = Extract<RcStep.Arguments, { argumentKind: 'CallSubroutine' }>;
    const a = args as CSArgs;
    this.checkRoutineID(a);
    this.checkSelfReference(a, routine);
    this.checkRoutineExists(a, ctx);
    this.checkNoWeld(a, ctx);
    this.checkSubroutineValid(a, ctx);
  }

  private checkRoutineID(
    args: Extract<RcStep.Arguments, { argumentKind: 'CallSubroutine' }>,
  ): void {
    if (!args.routineID) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Subroutine routine must be selected',
        fieldId: 'routineID',
      });
    }
  }

  /**
   * Forbid a routine from calling itself.
   */
  private checkSelfReference(
    args: Extract<RcStep.Arguments, { argumentKind: 'CallSubroutine' }>,
    routine: CanonicalValidateArgs['routine'],
  ): void {
    if (args.routineID && args.routineID === routine.routineId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'A routine cannot call itself as a subroutine',
        fieldId: 'routineID',
      });
    }
  }

  /**
   * Existence check: is the referenced routine in the caller's known
   * routine set? Skipped when the caller didn't supply
   * `ctx.knownRoutineIds`.
   */
  private checkRoutineExists(
    args: Extract<RcStep.Arguments, { argumentKind: 'CallSubroutine' }>,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    if (!args.routineID || !ctx.knownRoutineIds) {
      return;
    }

    if (!ctx.knownRoutineIds.has(args.routineID)) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Selected subroutine no longer exists',
        fieldId: 'routineID',
      });
    }
  }

  /**
   * A run cannot weld from inside a subroutine, so a call that reaches a
   * Weld step is blocked at the call boundary rather than in the routine
   * that holds the weld — that routine is free to weld when it runs
   * directly.
   *
   * `weldSource` names the routine holding the weld, which is not always
   * the routine this step calls: the reach is transitive, and telling an
   * operator to remove a Weld step from a routine that only forwards the
   * call leaves them with nothing to delete.
   *
   * Reported ahead of `checkSubroutineValid`: a welding subroutine with
   * other problems would satisfy both, and the ban is the finding that
   * fixing those other problems will not clear.
   *
   * @throws {ActionRequiredError}
   */
  private checkNoWeld(
    args: Extract<RcStep.Arguments, { argumentKind: 'CallSubroutine' }>,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    if (!args.routineID || !ctx.resolvedSubroutines) return;

    const entry = ctx.resolvedSubroutines.get(args.routineID);

    if (entry?.weldSource === undefined) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `A Weld step cannot run inside a subroutine. Remove the Weld step from "${entry.weldSource}".`,
      fieldId: 'routineID',
    });
  }

  /**
   * Validity check: is the referenced routine itself valid?
   * `precheckForExecution` synthesizes `ctx.resolvedSubroutines` from
   * `ctx.subroutines` (cascading invalidity through the call graph).
   * Skipped when the resolved map is absent or this id wasn't part of
   * the resolved graph (existence is reported separately by
   * `checkRoutineExists`).
   */
  private checkSubroutineValid(
    args: Extract<RcStep.Arguments, { argumentKind: 'CallSubroutine' }>,
    ctx: CanonicalValidateArgs['ctx'],
  ): void {
    if (!args.routineID || !ctx.resolvedSubroutines) return;

    const entry = ctx.resolvedSubroutines.get(args.routineID);

    if (!entry || entry.isValid) return;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: `"${entry.name}" has issues that prevent running`,
      fieldId: 'routineID',
    });
  }
}

export const CallSubroutineValidation: ValidationRule =
  new CallSubroutineRule().toValidationRule();
